import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { toast } from "@/utils/appToast";
import PhoneInput from 'react-phone-input-2';
import 'react-phone-input-2/lib/style.css';
import { api } from '@/utils/api';
import { updateUser } from '@/features/user/userSlice';
import { setAuthentication } from '@/shared/services/credentialsService';

const CompleteEssentialInfo: React.FC = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const location = useLocation();
    const dispatch = useDispatch();
    const { user } = useSelector((state: { user }) => state.user || {});

    const [phone, setPhone] = useState('');
    const [phoneError, setPhoneError] = useState('');
    const [smsNotificationsOptIn, setSmsNotificationsOptIn] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [userType, setUserType] = useState<'client' | 'worker'>('client');

    useEffect(() => {
        const type = location.state?.userType || user?.type || 'client';
        setUserType(type === 'worker' ? 'worker' : 'client');
    }, [location.state, user]);

    const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        setIsLoading(true);

        if (!phone || phone.replace(/\D/g, '').length < 11) {
            setPhoneError(t('pleaseEnterValidPhone') || 'Please enter a valid phone number');
            setIsLoading(false);
            return;
        }

        try {
            const phoneWithPlus = phone.startsWith('+') ? phone : `+${phone}`;
            const userUpdateData = {
                phone: phoneWithPlus,
                isEssentialCompleted: true,
                isSmsSent: smsNotificationsOptIn,
            };

            await api.updateProfile(userUpdateData as any);
            
            try {
                const profileResponse = await api.getProfileDetails();
                if (profileResponse && profileResponse.success && profileResponse.data) {
                    dispatch(updateUser(profileResponse.data));
                } else {
                    const updatedUser = { ...user, ...userUpdateData };
                    dispatch(updateUser(updatedUser));
                }
            } catch (err) {
                console.error('Error fetching updated profile:', err);
                const updatedUser = { ...user, ...userUpdateData };
                dispatch(updateUser(updatedUser));
            }

            toast.success(t('EssentialCompletedSuccessfully') || 'Profile updated successfully');
            setAuthentication(true, true);
            setTimeout(() => {
                if (userType === 'worker') {
                    navigate('/settings');
                } else {
                    navigate('/');
                }
            }, 1000);

        } catch (err: any) {
            console.error('Error updating profile:', err);
            toast.error(err?.message || t('errorUpdatingProfile') || 'Error updating profile. Please try again.');
        } finally {
            setIsLoading(false);
        }
    };

    const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

    useEffect(() => {
        const handleResize = () => {
            setIsMobile(window.innerWidth < 768);
        };
        window.addEventListener("resize", handleResize);
        return () => window.removeEventListener("resize", handleResize);
    }, []);

    return (
        <div
            style={{
                minHeight: "100vh",
                backgroundColor: "#f3f4f6",
                display: "flex",
                alignItems: "flex-start",
                justifyContent: "center",
                padding: "20px",
                paddingTop: "120px",
            }}
        >
            <div
                style={{
                    backgroundColor: "white",
                    borderRadius: "24px",
                    padding: isMobile ? "28px 24px" : "32px 40px",
                    maxWidth: "500px",
                    width: "100%",
                    display: "flex",
                    flexDirection: "column",
                }}
            >
                <div style={{ textAlign: "center", marginBottom: "24px" }}>
                    <div
                        style={{
                            display: "flex",
                            justifyContent: "center",
                            alignItems: "center",
                            marginBottom: "12px",
                        }}
                    >
                        <img
                            src="/images/phone_symbol.svg"
                            alt="Phone Symbol"
                            style={{
                                width: '50px',
                                height: '50px',
                                display: 'block'
                            }}
                        />
                    </div>
                    <h1
                        style={{
                            fontSize: isMobile ? "22px" : "26px",
                            fontWeight: 600,
                            color: "#1e293b",
                            margin: 0,
                            lineHeight: "1.3",
                        }}
                    >
                        {t('completeYourProfile') || 'Complete Your Profile'}
                    </h1>
                    <p style={{ color: "#64748b", fontSize: "14px", marginTop: "8px", marginBottom: 0 }}>
                        {t('completeClientProfileMessage') || 'Please provide your phone number to continue.'}
                    </p>
                </div>

                <form onSubmit={handleSubmit}>
                    <div className="form-group" style={{ display: "flex", flexDirection: "column", gap: "20px" }}>
                    <div className="form-group" style={{ marginBottom: 0 }}>
                        <label htmlFor="phone" style={{ color: "#1e293b", display: "block", fontSize: "14px", marginBottom: "6px" }}>
                            {t('phone')} <span style={{ color: "red" }}> *</span>
                        </label>
                        <PhoneInput
                            country={'us'}
                            countryCodeEditable={false}
                            value={phone}
                            onChange={value => {
                                setPhone(value);
                                setPhoneError('');
                            }}
                            inputProps={{
                                name: 'phone',
                                required: true,
                                id: 'phone',
                                style: { width: '100%', minHeight: '48px', fontSize: '15px' }
                            }}
                            inputStyle={{
                                width: '100%',
                                borderRadius: "8px",
                                minHeight: '48px',
                                fontSize: '15px',
                                border: "1px solid #e5e7eb"
                            }}
                            buttonStyle={{
                                borderRadius: "8px 0 0 8px"
                            }}
                            containerStyle={{
                                width: '100%'
                            }}
                            specialLabel=""
                            disableDropdown={false}
                            onlyCountries={['us']}
                            onBlur={() => {
                                if (phone && phone.replace(/\D/g, '').length < 11) {
                                    setPhoneError(t('pleaseEnterValidPhone') || 'Please enter a valid phone number');
                                }
                            }}
                        />
                        {phoneError && (
                            <p style={{ marginTop: "4px", fontSize: "12px", color: "#ef4444" }}>{phoneError}</p>
                        )}
                    </div>

                    <div className="form-group" style={{ marginBottom: 0 }}>
                        <div style={{ display: "flex", alignItems: "flex-start", gap: "8px" }}>
                            <input
                                type="checkbox"
                                id="smsOptIn"
                                checked={smsNotificationsOptIn}
                                onChange={(e) => setSmsNotificationsOptIn(e.target.checked)}
                                style={{ marginTop: "2px", flexShrink: 0 }}
                            />
                            <div>
                                <label htmlFor="smsOptIn" style={{ color: "#1e293b", fontSize: "14px", margin: 0, cursor: "pointer", lineHeight: 1.4 }}>
                                    {t('smsOptInCheckboxLabel')}
                                </label>
                                <p style={{ color: "#64748b", fontSize: "12px", margin: "6px 0 0 0" }}>
                                    {t('smsOptInHelperText')}
                                </p>
                            </div>
                        </div>
                    </div>

                    <button
                        type="submit"
                        disabled={isLoading}
                        style={{
                            backgroundColor: "#4285F4",
                            color: "white",
                            width: "100%",
                            padding: "14px 32px",
                            borderRadius: "12px",
                            border: "none",
                            fontSize: "16px",
                            fontWeight: 600,
                            cursor: isLoading ? "not-allowed" : "pointer",
                            opacity: isLoading ? 0.7 : 1,
                            marginTop: "8px"
                        }}
                    >
                        {isLoading ? (
                            <div
                                className="spinner-border"
                                style={{ height: "15px", width: "15px" }}
                                role="status"
                            ></div>
                        ) : (
                            t('continue') || 'Continue'
                        )}
                    </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default CompleteEssentialInfo;
